import urlJoin from 'url-join'

export const messagingSection = '/messaging/'

export const messagingHomeUrl = (): string => messagingSection

/**
 * The open conversation's read context, carried in the URL so it survives a
 * reload and a shared deep link. `unassigned` reads the caseless triage
 * conversation; `history` reads (read-only) a conversation the operator
 * previously handled but no longer owns; `supervised` reads any conversation
 * on MANAGE_CONVERSATIONS alone; `team` reads a peer's conversation from the
 * Supervised inbox; `operatorId` reads a peer's conversation (team-lead
 * cross-operator inbox — applies to the assigned and history reads). All
 * absent = the viewer's own assigned inbox.
 *
 * `team` reads the SAME server scope as the viewer's own inbox (ASSIGNED,
 * narrowed by `operator`) — it rides in the URL because the workspace derives
 * the visible inbox tab from `queue`. Without it, opening a row from the Team
 * inbox (including the desktop auto-select of the first row) dropped the
 * param and snapped the pane back to "My inbox".
 *
 * The queue values are mutually exclusive on the wire (they map to one server
 * scope), so `messagingConversationUrl` picks in a fixed precedence rather
 * than emitting two.
 */
export interface ThreadUrlContext {
  unassigned?: boolean
  history?: boolean
  supervised?: boolean
  team?: boolean
  operatorId?: string | undefined
}

export const messagingConversationUrl = (
  conversationId: string,
  context: ThreadUrlContext = {},
): string => {
  const base = urlJoin(messagingSection, encodeURIComponent(conversationId))
  const params = new URLSearchParams()
  if (context.unassigned) {
    params.set('queue', 'unassigned')
  } else if (context.supervised) {
    params.set('queue', 'supervised')
  } else if (context.history) {
    params.set('queue', 'history')
  } else if (context.team) {
    params.set('queue', 'team')
  }

  // Meaningless alongside `supervised` (which resolves by permission, not by
  // whose set the thread is in) and alongside `unassigned` (which the server
  // ignores it for) — so it rides only where it changes the read.
  if (context.operatorId && !context.supervised && !context.unassigned) {
    params.set('operator', context.operatorId)
  }

  const qs = params.toString()
  return qs ? `${base}?${qs}` : base
}
